import React from 'react';
import { Card, Skeleton, SkeletonButton, SkeletonInput, SkeletonText } from '@parsina/framework';
import { C } from '../../constants/theme';
import Panel from '../../components/common/Panel.jsx';
import {
  SkeletonPage, InnerHeaderSkeleton, MobileHeaderSkeleton,
} from '../../components/common/skeletons.jsx';

// نسخه — the loading shape of the prescription screen while the patient record
// (allergies, medication history) resolves.
//
// Desktop: InnerPageHeader band with the ثبت button, then the two-column grid
// Prescription.web.jsx draws — the search card and the empty prescription on
// the wide side, the interactions and history panels in the 320px rail.
// Mobile: the gradient band, the search field, the empty prescription card,
// the interactions card, the collapsed history, and the docked commit row.
// The geometry is the real one, so the search field the dentist is about to
// type into does not move when the record lands.

export const PrescriptionSkeletonWeb = ({ label }) => (
  <SkeletonPage
    label={label}
    data-testid="rx-content"
    style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}
  >
    <InnerHeaderSkeleton avatar={false} meta actions={1} />
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: '20px 28px 40px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 18, alignItems: 'start' }}>
        <div>
          <Card style={{ marginBottom: 14 }}>
            <Skeleton width={110} height={12} style={{ marginBottom: 8 }} />
            <SkeletonInput label={false} />
          </Card>
          <Card style={{ minHeight: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <Skeleton width={44} height={44} radius={12} />
            <Skeleton width={160} height={14} />
            <Skeleton width={220} height={12} />
          </Card>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Panel title={<Skeleton width={96} height={14} />}>
            <SkeletonText lines={2} height={12} lastWidth="70%" />
          </Panel>
          <Panel title={<Skeleton width={110} height={14} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <Skeleton width={`${45 + ((i * 17) % 35)}%`} height={13} />
                  <Skeleton width="40%" height={11} />
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  </SkeletonPage>
);

export const PrescriptionSkeletonMobile = ({ label }) => (
  <SkeletonPage
    label={label}
    data-testid="rx-content"
    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
  >
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <MobileHeaderSkeleton back subtitle />
      <div style={{ padding: '0.875rem 0.75rem 0' }}>
        {/* MobileDrugSearch's trigger: a 44px search field. */}
        <SkeletonInput label={false} />
        <Card style={{ marginTop: '0.875rem', minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <Skeleton width={44} height={44} radius={12} />
          <Skeleton width={140} height={14} />
          <Skeleton width={200} height={12} />
        </Card>
        <Card style={{ marginTop: '0.875rem', padding: '0.875rem' }}>
          <Skeleton width={96} height={13} style={{ marginBottom: 8 }} />
          <SkeletonText lines={2} height={12} lastWidth="60%" />
        </Card>
        {/* The collapsed history section: a 44px disclosure header. */}
        <div style={{ marginTop: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44 }}>
          <Skeleton width={110} height={13} />
          <Skeleton width={16} height={16} radius={4} />
        </div>
      </div>
    </div>
    <div style={{ flexShrink: 0, padding: '0.75rem 0.75rem calc(0.75rem + env(safe-area-inset-bottom, 0px))', borderTop: `1px solid ${C.border}`, background: C.card }}>
      <SkeletonButton width="100%" />
    </div>
  </SkeletonPage>
);
